// Kit — the retailer PA AI. Right-side slide-in panel with a morning-briefing
// summary, dismissible proactive-alert cards, and a streaming chat.
//
// Singleton: openKit() lazily builds the panel once and opens it. Both the
// "Ask Kit" header button and the dashboard briefing widget call openKit();
// the dashboard's "offer to act" buttons call openKit(prompt) to pre-send a
// message (e.g. "Draft a follow-up for the Johnson quote").
//
// Backend: GET /api/portal/kit/briefing (JSON) + POST /api/portal/kit/chat
// (SSE stream). Auth is the same-origin kos_portal cookie; retailerId is the
// cached slug. Streaming uses raw fetch (the api-client doesn't stream).

import { api } from "@tkos/api-client";
import { iconMarkup, type IconName } from "@tkos/shared-ui";
import { getCachedUser } from "./auth";
import { fetchActiveAlerts, renderAlertCard, type KitAlert } from "./kit-alerts";
import "./kit-panel.css";

interface BriefingItem { [k: string]: unknown }
export interface Briefing {
  greetingName: string;
  quotes_needing_followup: Array<{ reference?: string; value?: number; days?: number; customer?: string }>;
  overdue_deposits: Array<{ amount?: number; customer?: string; reference?: string; stage?: string }>;
  uncontacted_leads: BriefingItem[];
  deliveries_due_48hrs: Array<{ reference?: string; customer?: string }>;
  unconfirmed_appointments: Array<{ type?: string; customer?: string }>;
  unread_messages: number;
  open_snags: BriefingItem[];
  counts: Record<string, number>;
}

export interface Alert {
  id: string;
  icon: IconName;
  title: string;
  detail: string;
  /** Pre-filled message sent to Kit when the action button is clicked. */
  action?: { label: string; prompt: string };
}

type ChatMsg = { role: "user" | "assistant"; content: string };

/** A proposed write action returned by the tool-use chat, awaiting confirm. */
interface PendingAction {
  tool: string;
  preview: string;
  execute: { method: string; path: string; body: Record<string, unknown> };
  confirmation: string;
  /** Alerts to auto-dismiss after this action executes (resolves them). */
  autoDismiss?: { alertTypes?: string[]; customerId?: string; jobId?: string; email?: string };
}
interface KitChatResponse {
  ok: boolean;
  reply: string;
  pendingActions: PendingAction[];
  model?: string;
}

const gbp = (n?: number): string =>
  typeof n === "number" && isFinite(n)
    ? "£" + Math.round(n).toLocaleString("en-GB")
    : "";

/** Fetch the morning briefing for a retailer. Returns null on failure. */
export async function fetchKitBriefing(retailerId: string): Promise<Briefing | null> {
  try {
    const resp = await api<{ ok: boolean; briefing: Briefing }>(
      `/portal/kit/briefing?retailerId=${encodeURIComponent(retailerId)}`,
    );
    return resp?.ok ? resp.briefing : null;
  } catch {
    return null;
  }
}

export function deriveAlerts(b: Briefing): Alert[] {
  const out: Alert[] = [];
  const c = b.counts || ({} as Record<string, number>);

  if (c.quotes_needing_followup > 0) {
    const eg = b.quotes_needing_followup[0];
    out.push({
      id: "quotes",
      icon: "file",
      title: `${c.quotes_needing_followup} quote${c.quotes_needing_followup === 1 ? "" : "s"} not followed up in 7+ days`,
      detail: eg ? `Oldest: ${eg.customer || eg.reference || "a quote"}${eg.days ? ` (${eg.days} days)` : ""}${eg.value ? ` · ${gbp(eg.value)}` : ""}` : "",
      action: { label: "Draft a follow-up", prompt: "Draft a short, friendly follow-up message for my oldest unanswered quote." },
    });
  }
  if (c.overdue_deposits > 0) {
    const eg = b.overdue_deposits[0];
    out.push({
      id: "deposits",
      icon: "pound",
      title: `${c.overdue_deposits} deposit${c.overdue_deposits === 1 ? "" : "s"} overdue`,
      detail: eg ? `${eg.customer || eg.reference || "Customer"}${eg.amount ? ` — ${gbp(eg.amount)}` : ""}` : "",
      action: { label: "Draft a deposit reminder", prompt: "Draft a polite deposit reminder for my most overdue deposit." },
    });
  }
  if (c.uncontacted_leads > 0) {
    out.push({
      id: "leads",
      icon: "customer",
      title: `${c.uncontacted_leads} lead${c.uncontacted_leads === 1 ? "" : "s"} need contacting`,
      detail: "Not contacted in 7+ days. Speed to lead wins jobs.",
      action: { label: "How should I work these?", prompt: "What's the quickest way to work through my uncontacted leads today?" },
    });
  }
  if (c.deliveries_due_48hrs > 0) {
    out.push({
      id: "deliveries",
      icon: "truck",
      title: `${c.deliveries_due_48hrs} delivery${c.deliveries_due_48hrs === 1 ? "" : "ies"} due in 48h with no driver`,
      detail: b.deliveries_due_48hrs[0]?.customer || "",
      action: { label: "Flag for attention", prompt: "Remind me what I need to sort for my unassigned deliveries due in the next 48 hours." },
    });
  }
  if (c.unconfirmed_appointments > 0) {
    const eg = b.unconfirmed_appointments[0];
    out.push({
      id: "appts",
      icon: "calendar",
      title: `${c.unconfirmed_appointments} appointment${c.unconfirmed_appointments === 1 ? "" : "s"} not yet confirmed`,
      detail: eg ? `${eg.type || "Appointment"}${eg.customer ? ` — ${eg.customer}` : ""}` : "",
      action: { label: "Draft a confirmation", prompt: "Draft a short message to confirm my next unconfirmed appointment." },
    });
  }
  if (c.unread_messages > 0) {
    out.push({
      id: "messages",
      icon: "chat",
      title: `${c.unread_messages} unread customer message${c.unread_messages === 1 ? "" : "s"}`,
      detail: "Waiting on a reply.",
    });
  }
  if (c.open_snags > 0) {
    out.push({
      id: "snags",
      icon: "alert-triangle",
      title: `${c.open_snags} open snag${c.open_snags === 1 ? "" : "s"}`,
      detail: "Unresolved — get them closed off.",
    });
  }
  return out;
}

function briefingSummaryText(b: Briefing): string {
  const c = b.counts || ({} as Record<string, number>);
  const bits: string[] = [];
  if (c.quotes_needing_followup) bits.push(`${c.quotes_needing_followup} quote${c.quotes_needing_followup === 1 ? "" : "s"} not followed up in over a week`);
  if (c.overdue_deposits) bits.push(`${c.overdue_deposits} deposit${c.overdue_deposits === 1 ? "" : "s"} overdue`);
  if (c.unconfirmed_appointments) bits.push(`${c.unconfirmed_appointments} appointment${c.unconfirmed_appointments === 1 ? "" : "s"} not yet confirmed`);
  if (c.uncontacted_leads) bits.push(`${c.uncontacted_leads} lead${c.uncontacted_leads === 1 ? "" : "s"} not contacted`);
  if (c.deliveries_due_48hrs) bits.push(`${c.deliveries_due_48hrs} delivery with no driver`);
  if (c.unread_messages) bits.push(`${c.unread_messages} unread message${c.unread_messages === 1 ? "" : "s"}`);
  if (c.open_snags) bits.push(`${c.open_snags} open snag${c.open_snags === 1 ? "" : "s"}`);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  if (!bits.length) {
    return `${greet} ${b.greetingName}. Nothing urgent on the board right now — quotes, deposits, leads and deliveries all look tidy. Ask me anything when you need me.`;
  }
  return `${greet} ${b.greetingName}. Here's what needs your attention today:\n\n` +
    bits.map((x) => `• ${x}`).join("\n") +
    `\n\nWant me to help with any of these?`;
}

// ── Singleton controller ─────────────────────────────────────────────────────
let panel: KitPanel | null = null;

export function openKit(prompt?: string): void {
  if (!panel) panel = new KitPanel();
  panel.open(prompt);
}

class KitPanel {
  private root: HTMLElement;
  private backdrop: HTMLElement;
  private alertsEl!: HTMLElement;
  private logEl!: HTMLElement;
  private inputEl!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private history: ChatMsg[] = [];
  private loadedBriefing = false;
  private streaming = false;
  private dismissed = new Set<string>();

  constructor() {
    this.backdrop = document.createElement("div");
    this.backdrop.className = "kit-backdrop";
    this.backdrop.addEventListener("click", () => this.close());

    this.root = document.createElement("aside");
    this.root.className = "kit-panel";
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-label", "Kit — your assistant");
    this.root.innerHTML = `
      <header class="kit-panel__head">
        <span class="kit-panel__title">${iconMarkup("sparkle", { size: "sm" })}<strong>Kit</strong><span class="kit-panel__sub">your PA</span></span>
        <button type="button" class="kit-panel__close" aria-label="Close Kit">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="kit-panel__alerts" data-role="alerts"></div>
      <div class="kit-panel__log" data-role="log" aria-live="polite"></div>
      <form class="kit-panel__compose" data-role="compose">
        <textarea data-role="input" rows="1" placeholder="Ask Kit anything…" aria-label="Message Kit"></textarea>
        <button type="submit" class="kit-panel__send" aria-label="Send" data-role="send">${iconMarkup("send", { size: "sm" })}</button>
      </form>
    `;

    this.alertsEl = this.root.querySelector('[data-role="alerts"]')!;
    this.logEl = this.root.querySelector('[data-role="log"]')!;
    this.inputEl = this.root.querySelector('[data-role="input"]')!;
    this.sendBtn = this.root.querySelector('[data-role="send"]')!;

    this.root.querySelector(".kit-panel__close")!.addEventListener("click", () => this.close());
    this.root.querySelector('[data-role="compose"]')!.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submit(this.inputEl.value);
    });
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.submit(this.inputEl.value); }
    });
    this.inputEl.addEventListener("input", () => {
      this.inputEl.style.height = "auto";
      this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + "px";
    });
    document.addEventListener("keydown", this.onEsc);

    document.body.append(this.backdrop, this.root);
  }

  private onEsc = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.root.classList.contains("is-open")) this.close();
  };

  open(prompt?: string): void {
    this.root.classList.add("is-open");
    this.backdrop.classList.add("is-open");
    if (!this.loadedBriefing) void this.loadBriefing();
    if (prompt && prompt.trim()) {
      // Defer so the panel paint settles before we stream.
      window.setTimeout(() => this.submit(prompt), 60);
    } else {
      window.setTimeout(() => this.inputEl.focus(), 80);
    }
  }

  close(): void {
    this.root.classList.remove("is-open");
    this.backdrop.classList.remove("is-open");
  }

  private retailerId(): string {
    return getCachedUser()?.retailerId || "";
  }

  private async loadBriefing(): Promise<void> {
    this.loadedBriefing = true;
    const rid = this.retailerId();
    // Greeting text comes from the briefing; the alert CARDS come from the
    // persisted kit_alerts (single source of truth). Fetch both in parallel.
    const [briefing, alerts] = await Promise.all([
      api<{ ok: boolean; briefing: Briefing }>(`/portal/kit/briefing?retailerId=${encodeURIComponent(rid)}`).catch(() => null),
      fetchActiveAlerts(rid).catch(() => [] as KitAlert[]),
    ]);
    this.renderPersistedAlerts(alerts || []);
    if (briefing?.ok) {
      this.addMessage("assistant", briefingSummaryText(briefing.briefing));
    } else {
      this.addMessage("assistant", "Morning. I couldn't pull your briefing just now — try reopening me in a moment. You can still ask me anything.");
    }
  }

  /** Render the persisted alert stack above the conversation, urgency-first. */
  private renderPersistedAlerts(alerts: KitAlert[]): void {
    this.alertsEl.innerHTML = "";
    const visible = alerts.filter((a) => !this.dismissed.has(a.id));
    if (!visible.length) return;
    const rid = this.retailerId();
    for (const a of visible) {
      renderAlertCard(
        this.alertsEl, a, rid,
        (id) => {
          this.dismissed.add(id);
          this.alertsEl.querySelector(`[data-alert-id="${CSS.escape(id)}"]`)?.remove();
        },
        () => this.close(),
      );
    }
  }

  private addMessage(role: "user" | "assistant", text: string): HTMLElement {
    const el = document.createElement("div");
    el.className = `kit-msg kit-msg--${role}`;
    el.textContent = text;
    this.logEl.appendChild(el);
    this.logEl.scrollTop = this.logEl.scrollHeight;
    return el;
  }

  private async submit(raw: string): Promise<void> {
    const message = (raw || "").trim();
    if (!message || this.streaming) return;
    this.inputEl.value = "";
    this.inputEl.style.height = "auto";
    this.addMessage("user", message);
    this.history.push({ role: "user", content: message });

    this.streaming = true;
    this.sendBtn.disabled = true;
    const thinking = this.addMessage("assistant", "…");
    thinking.classList.add("is-streaming");

    try {
      const resp = await api<KitChatResponse>(
        `/kit/actions/chat?retailerId=${encodeURIComponent(this.retailerId())}`,
        { method: "POST", body: JSON.stringify({ message, history: this.history.slice(-12) }) },
      );
      thinking.remove();
      const reply = (resp?.reply || "").trim() || "On it.";
      this.addMessage("assistant", reply);
      this.history.push({ role: "assistant", content: reply });
      // One or more write actions wait for a single confirmation.
      if (resp?.pendingActions?.length) this.renderPendingActions(resp.pendingActions);
    } catch {
      thinking.remove();
      this.addMessage("assistant", "Sorry mate, I couldn't get through just then. Give it another go in a moment.");
    } finally {
      this.streaming = false;
      this.sendBtn.disabled = false;
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }
  }

  /**
   * Inline Confirm/Cancel for one OR MORE proposed write actions. A compound
   * instruction yields several — a single Confirm runs them in sequence,
   * reporting each, and auto-dismisses any alerts they resolve.
   */
  private renderPendingActions(actions: PendingAction[]): void {
    const rid = this.retailerId();
    const card = document.createElement("div");
    card.className = "kit-confirm";
    const label = actions.length > 1 ? `Confirm all ${actions.length}` : "Confirm";
    card.innerHTML = `
      <div class="kit-confirm__row">
        <button type="button" class="kit-confirm__yes" data-role="confirm">${label}</button>
        <button type="button" class="kit-confirm__no" data-role="cancel">Cancel</button>
      </div>`;
    this.logEl.appendChild(card);
    this.logEl.scrollTop = this.logEl.scrollHeight;

    const confirmBtn = card.querySelector<HTMLButtonElement>('[data-role="confirm"]')!;
    const cancelBtn = card.querySelector<HTMLButtonElement>('[data-role="cancel"]')!;
    const settle = (): void => { confirmBtn.disabled = true; cancelBtn.disabled = true; card.remove(); };
    const say = (text: string): void => { this.addMessage("assistant", text); this.history.push({ role: "assistant", content: text }); };

    cancelBtn.addEventListener("click", () => {
      settle();
      say(actions.length > 1 ? "No bother — left all that alone." : "No bother — left it alone.");
    });

    confirmBtn.addEventListener("click", () => {
      confirmBtn.disabled = true; cancelBtn.disabled = true;
      confirmBtn.textContent = "Working…";
      void (async () => {
        settle();
        for (const pending of actions) {
          try {
            const ex = pending.execute;
            await api(`${ex.path}${ex.path.includes("?") ? "&" : "?"}retailerId=${encodeURIComponent(rid)}`, {
              method: ex.method,
              body: JSON.stringify(ex.body ?? {}),
            });
            // Auto-dismiss any alert this action resolved (best-effort).
            if (pending.autoDismiss) {
              await api(`/kit/actions/alerts/dismiss-related?retailerId=${encodeURIComponent(rid)}`, {
                method: "POST", body: JSON.stringify(pending.autoDismiss),
              }).catch(() => {});
            }
            say(pending.confirmation || "Done.");
          } catch {
            say("That one didn't go through — give it another go in a moment.");
          }
        }
        this.logEl.scrollTop = this.logEl.scrollHeight;
      })();
    });
  }
}
